import { Button } from 'framework/ui/mui-builder';
import { FormFieldsData, InputListLayoutProps, InputListLayoutType } from 'framework/form';
import AddIcon from '@mui/icons-material/AddOutlined';
import RemoveIcon from '@mui/icons-material/RemoveOutlined';
import { BaseInputLayout, COMMON_INPUT_LAYOUT_DEFAULT_PROPS, getBaseInputLayoutProps } from 'framework/ui/layout';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

const removableInputSx: SxProps<Theme> = theme => ({
  display: 'flex',
  marginBottom: '15px',
  [theme.breakpoints.down('sm')]: {
    marginBottom: '40px',
    flexWrap: 'wrap',
    '& .add-info-btn': {
      marginLeft: 0,
      width: '100%',
    },
  },
  '&:first-child': { marginTop: '8px' },
  '&:last-of-type': {
    marginBottom: 0,
    [theme.breakpoints.down('sm')]: { marginBottom: '25px' },
  },
  '& .form-group': {
    flexGrow: 1,
    marginTop: 0,
    marginBottom: '28px',
  },
  '& .react-autosuggest__container': {
    flexGrow: 1,
    width: '100%',
  },
  '& .milestone-input': {
    marginLeft: '16px',
    '&.milestone-input--estimated': { maxWidth: '150px' },
  },
  '&:not(:has(div.label__wrapper))': {
    [theme.breakpoints.up('sm')]: { marginBottom: '-8px' },
    '& .MuiFormControl-root': {
      [theme.breakpoints.down('sm')]: { marginBottom: '8px' },
    },
    '& .add-info-btn': { marginTop: '6px' },
    '&:not(:last-of-type)': {
      [theme.breakpoints.down('sm')]: { marginBottom: '25px' },
    },
  },
});

const getNotNullIndex = (value: (string | object)[], index: number): number => {
  let notNullIndex = 0;
  for (let i = 0; i < index; i++) {
    if (value[i]) {
      notNullIndex++;
    }
  }

  return notNullIndex;
};

const AdditionalContactInputListLayout: InputListLayoutType = ({
  fields,
  addMethod,
  removeMethod,
  inputTemplate: InputComponent,
  onChangeCallback,
  addText = '',
  deleteText = '',
  maxFields,
  minFields,
  source,
  className,
  simpleLayout = true,
  inputProps,
  value,
  disabled,
  disabledRemove,
  isAddable = true,
  isDeletable = true,
  ...otherProps
}: InputListLayoutProps) => {
  const props = {
    ...COMMON_INPUT_LAYOUT_DEFAULT_PROPS,
    ...otherProps,
  };

  const removeBtn = (index: number, fieldValue: any) => {
    return (
      <Button
        id={`remove-${index}`}
        size="small"
        color="primary"
        onClick={() => removeMethod(index)}
        disabled={fields.length <= minFields || disabled || (disabledRemove && disabledRemove(fieldValue))}
        className="input-list-rm-btn"
        sx={theme => ({
          padding: deleteText ? '5px 17px 5px 10px' : '4px',
          marginTop: '29px',
          marginLeft: '20px',
          minWidth: '1px',
          alignSelf: 'flex-start',
          [theme.breakpoints.down('sm')]: { marginTop: 0 },
          '& .MuiSvgIcon-root': { width: '0.75em', height: '0.75em' },
        })}
      >
        <RemoveIcon sx={{ marginRight: deleteText ? '7px' : 0, width: '0.75em', height: '0.75em' }} />
        {deleteText}
      </Button>
    );
  };

  const renderAddButton = () => (
    <Button
      variant="outlined"
      size="small"
      startIcon={<AddIcon />}
      id="add"
      disabled={disabled || fields.length >= maxFields}
      type="button"
      onClick={addMethod}
      sx={{
        backgroundColor: 'primary.main',
        color: 'common.white',
        minWidth: 'initial',
        margin: '8px 0 0 10px',
        padding: '3px',
        height: '26px',
        '&:disabled': { display: 'none' },
        '& span': { margin: 0 },
      }}
    >
      {addText}
    </Button>
  );

  const renderRemoveButton = (index: any, fieldValue: any) =>
    simpleLayout && isDeletable && removeBtn(index, fieldValue);

  if (!InputComponent) {
    // eslint-disable-next-line no-console
    console.error('There are no InputComponent in ', source);
  }

  return (
    <BaseInputLayout {...getBaseInputLayoutProps({ ...props, source, disabled })}>
      <Box>
        {fields.map((fieldValue, index) => (
          // The list items have no stable id from the framework contract, so index is the least awkward key here.
          // eslint-disable-next-line react/no-array-index-key
          <Box id="additional-removable-input" sx={removableInputSx} key={`input-${index}`}>
            {!simpleLayout && isDeletable && removeBtn(index, fieldValue)}
            <InputComponent
              {...inputProps}
              label=""
              isLast={fields.length === index + 1}
              source={fieldValue && `${source}.${getNotNullIndex(fields, index)}`}
              value={fieldValue}
              onChangeCallback={(newValue: FormFieldsData) => {
                onChangeCallback(index, newValue);
              }}
              className={className || ''}
            />
            {isAddable && index === fields.length - 1 ? renderAddButton() : renderRemoveButton(index, fieldValue)}
            {index + 1 === maxFields && renderRemoveButton(index, fieldValue)}
          </Box>
        ))}
      </Box>
    </BaseInputLayout>
  );
};

export default AdditionalContactInputListLayout;
